import {
  Badge,
  Button,
  Card,
  Group,
  Menu,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDots, IconPlant2, IconPlus, IconShare, IconTrash, IconWand } from '@tabler/icons-react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '~/Auth'
import { useDeletePlaylist } from '~/api/mutations'
import { usePlaylists } from '~/api/queries'
import { formatDurationLong } from '~/lib/format'

export const Route = createFileRoute('/_app/playlists/')({
  component: PlaylistsPage,
})

function PlaylistsPage() {
  const { data, isLoading } = usePlaylists({ limit: 100 })
  const { session } = useAuth()
  const currentUserId = session.data?.user?.id != null ? Number(session.data.user.id) : undefined
  const deletePlaylist = useDeletePlaylist()

  const handleDelete = async (e: React.MouseEvent, playlistId: number) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await deletePlaylist.mutateAsync(playlistId)
      notifications.show({ title: 'Deleted', message: 'Playlist deleted', color: 'green' })
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Playlists</Title>
        <Group gap="xs">
          <Button
            renderRoot={(props) => <Link {...props} to="/playlists/generate" />}
            variant="light"
            leftSection={<IconWand size={16} />}
          >
            Generate
          </Button>
          <Button
            renderRoot={(props) => <Link {...props} to="/playlists/generate-seed" />}
            variant="light"
            leftSection={<IconPlant2 size={16} />}
          >
            Seed Generate
          </Button>
          <Button
            renderRoot={(props) => <Link {...props} to="/playlists/new" />}
            leftSection={<IconPlus size={16} />}
          >
            New Playlist
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Skeleton h={200} />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {data?.data.map((playlist) => {
            const isOwned = currentUserId != null && playlist.ownerId === currentUserId
            return (
              <Card
                key={playlist.id}
                renderRoot={(props) => (
                  <Link
                    {...props}
                    to="/playlists/$playlistId"
                    params={{ playlistId: String(playlist.id) }}
                  />
                )}
                withBorder
                padding="md"
                style={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                <Group justify="space-between" mb={4} wrap="nowrap">
                  <Text fw={500} truncate="end" style={{ flex: 1 }}>
                    {playlist.name}
                  </Text>
                  <Group gap={4} wrap="nowrap">
                    {!isOwned && (
                      <Badge
                        variant="light"
                        color="blue"
                        size="xs"
                        leftSection={<IconShare size={10} />}
                      >
                        Shared
                      </Badge>
                    )}
                    {isOwned && (
                      <Menu withinPortal position="bottom-end">
                        <Menu.Target>
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            px={4}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                          >
                            <IconDots size={14} />
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={(e) => handleDelete(e, playlist.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Group>
                <Text size="sm" c="dimmed">
                  {playlist.trackCount} tracks &middot; {formatDurationLong(playlist.duration)}
                </Text>
                {playlist.comment && (
                  <Text size="xs" c="dimmed" mt="xs" lineClamp={2}>
                    {playlist.comment}
                  </Text>
                )}
                <Text size="xs" c="dimmed" mt="xs">
                  by {playlist.owner}
                </Text>
              </Card>
            )
          })}
        </SimpleGrid>
      )}
    </Stack>
  )
}
